import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';
import type {
  CatalogueTest,
  FeedMessage,
  ImagingReferral,
  LabDashboard,
  LabOrder,
  LabResultInput,
  OutsideReport,
  OutsideRow,
  Prescription,
  PrescriptionItemInput,
  ResultsInbox,
  SpecimenLabel,
  TrendPoint,
} from './types';

const LIVE_MS = 10_000;

// ------------------------------------------------------------ prescriptions

export function usePrescriptions(encounterId: string) {
  return useQuery({
    queryKey: ['prescriptions', encounterId],
    queryFn: async ({ signal }) =>
      (
        await api.get<Prescription[]>(
          `/encounters/${encounterId}/prescriptions`,
          { signal },
        )
      ).data,
  });
}

export function usePrescriptionActions(encounterId: string) {
  const qc = useQueryClient();
  const onSuccess = () =>
    void qc.invalidateQueries({ queryKey: ['prescriptions', encounterId] });
  return {
    addItem: useMutation({
      mutationFn: async (input: PrescriptionItemInput) =>
        (
          await api.post<Prescription>(
            `/encounters/${encounterId}/prescription/items`,
            input,
          )
        ).data,
      onSuccess,
    }),
    removeItem: useMutation({
      mutationFn: async (v: { id: string; itemId: string }) =>
        (
          await api.post<Prescription>(
            `/prescriptions/${v.id}/items/${v.itemId}/remove`,
          )
        ).data,
      onSuccess,
    }),
    sign: useMutation({
      mutationFn: async (v: { id: string; version: number }) =>
        (
          await api.post<Prescription>(`/prescriptions/${v.id}/sign`, {
            version: v.version,
          })
        ).data,
      onSuccess,
    }),
    cancel: useMutation({
      mutationFn: async (v: { id: string; reason: string }) =>
        (
          await api.post<Prescription>(`/prescriptions/${v.id}/cancel`, {
            reason: v.reason,
          })
        ).data,
      onSuccess,
    }),
  };
}

/** GET /patients/:id/prescriptions/cancellable (API RL-1837). */
export interface CancellablePrescription {
  id: string;
  number: string | null;
  signedAt: string | null;
  prescriber: { id: string; fullName: string };
  branch: { id: string; name: string };
  source: string;
  medicines: string[];
}

/** RL-1837: the clinic manager's list of a patient's undispensed prescriptions. */
export function useCancellablePrescriptions(
  patientId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['cancellable-rx', patientId],
    enabled,
    queryFn: async ({ signal }) =>
      (
        await api.get<CancellablePrescription[]>(
          `/patients/${patientId}/prescriptions/cancellable`,
          { signal },
        )
      ).data,
  });
}

export function useCancelPrescription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; reason: string }) =>
      (
        await api.post<Prescription>(`/prescriptions/${v.id}/cancel`, {
          reason: v.reason,
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cancellable-rx'] });
      void qc.invalidateQueries({ queryKey: ['prescriptions'] });
    },
  });
}

// ------------------------------------------------------------ lab orders

export function useEncounterLabOrders(encounterId: string) {
  return useQuery({
    queryKey: ['lab-orders', 'encounter', encounterId],
    queryFn: async ({ signal }) =>
      (
        await api.get<LabOrder[]>(`/encounters/${encounterId}/lab-orders`, {
          signal,
        })
      ).data,
  });
}

export function useLabWorklist(status: string) {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: ['lab-orders', 'worklist', branch, status],
    queryFn: async ({ signal }) =>
      (await api.get<LabOrder[]>('/lab-orders', { signal, query: { status } }))
        .data,
    enabled: branch !== 'all',
    refetchInterval: LIVE_MS,
  });
}

export function useResultsToReview() {
  return useQuery({
    queryKey: ['lab-orders', 'to-review'],
    queryFn: async ({ signal }) =>
      (await api.get<LabOrder[]>('/results/to-review', { signal })).data,
    refetchInterval: LIVE_MS,
  });
}

export function useLabOrder(id: string | null) {
  return useQuery({
    queryKey: ['lab-order', id],
    queryFn: async ({ signal }) =>
      (await api.get<LabOrder>(`/lab-orders/${id}`, { signal })).data,
    enabled: !!id,
  });
}

export function useLabActions() {
  const qc = useQueryClient();
  const onSuccess = (o: LabOrder) => {
    qc.setQueryData(['lab-order', o.id], o);
    void qc.invalidateQueries({ queryKey: ['lab-orders'] });
  };
  const post = (path: string, body?: unknown) =>
    api.post<LabOrder>(path, body).then((r) => r.data);
  return {
    addTest: useMutation({
      mutationFn: (v: { encounterId: string; testName: string }) =>
        post(`/encounters/${v.encounterId}/lab-order/tests`, {
          testName: v.testName,
        }),
      onSuccess,
    }),
    removeTest: useMutation({
      mutationFn: (v: { id: string; itemId: string }) =>
        post(`/lab-orders/${v.id}/tests/${v.itemId}/remove`),
      onSuccess,
    }),
    send: useMutation({
      mutationFn: (v: { id: string; version: number; clinicalNote?: string }) =>
        post(`/lab-orders/${v.id}/send`, {
          version: v.version,
          clinicalNote: v.clinicalNote,
        }),
      onSuccess,
    }),
    cancel: useMutation({
      mutationFn: (v: { id: string; reason: string }) =>
        post(`/lab-orders/${v.id}/cancel`, { reason: v.reason }),
      onSuccess,
    }),
    collect: useMutation({
      mutationFn: (id: string) => post(`/lab-orders/${id}/collect`),
      onSuccess,
    }),
    dispatch: useMutation({
      mutationFn: (id: string) => post(`/lab-orders/${id}/dispatch`),
      onSuccess,
    }),
    recordResult: useMutation({
      mutationFn: (v: { id: string; itemId: string; input: LabResultInput }) =>
        post(`/lab-orders/${v.id}/tests/${v.itemId}/result`, v.input),
      onSuccess,
    }),
    resultsReady: useMutation({
      mutationFn: (id: string) => post(`/lab-orders/${id}/results-ready`),
      onSuccess,
    }),
    review: useMutation({
      mutationFn: (v: { id: string; comment?: string }) =>
        post(`/lab-orders/${v.id}/review`, { comment: v.comment }),
      onSuccess,
    }),
    release: useMutation({
      mutationFn: (v: {
        id: string;
        itemId: string;
        comment?: string;
        callNote?: string;
      }) =>
        post(`/lab-orders/${v.id}/tests/${v.itemId}/release`, {
          comment: v.comment,
          callNote: v.callNote,
        }),
      onSuccess,
    }),
    hold: useMutation({
      mutationFn: (v: { id: string; itemId: string; callNote?: string }) =>
        post(`/lab-orders/${v.id}/tests/${v.itemId}/hold`, {
          callNote: v.callNote,
        }),
      onSuccess,
    }),
    homeVisit: useMutation({
      mutationFn: (v: {
        id: string;
        at: string;
        place: string;
        version: number;
      }) =>
        post(`/lab-orders/${v.id}/home-visit`, {
          at: v.at,
          place: v.place,
          version: v.version,
        }),
      onSuccess,
    }),
    labelsPrinted: useMutation({
      mutationFn: (id: string) => post(`/lab-orders/${id}/labels/printed`),
      onSuccess,
    }),
    paperResults: useMutation({
      mutationFn: (v: {
        id: string;
        results: (LabResultInput & { itemId: string })[];
        markReady: boolean;
      }) =>
        post(`/lab-orders/${v.id}/paper-results`, {
          results: v.results,
          markReady: v.markReady,
        }),
      onSuccess,
    }),
  };
}

// ------------------------------------------------------------ lab desk (RL-724)

export function useLabDashboard(enabled = true) {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: ['lab-orders', 'dashboard', branch],
    queryFn: async ({ signal }) =>
      (await api.get<LabDashboard>('/lab/dashboard', { signal })).data,
    enabled: enabled && branch !== 'all',
    refetchInterval: LIVE_MS,
  });
}

export function useSpecimenLabels(id: string | null) {
  return useQuery({
    queryKey: ['lab-labels', id],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ labels: SpecimenLabel[] }>(`/lab-orders/${id}/labels`, {
          signal,
        })
      ).data.labels,
    enabled: !!id,
  });
}

export function useResultsInbox() {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: ['lab-orders', 'inbox', branch],
    queryFn: async ({ signal }) =>
      (await api.get<ResultsInbox>('/lab/results-inbox', { signal })).data,
    enabled: branch !== 'all',
    refetchInterval: LIVE_MS,
  });
}

export function useFeedActions() {
  const qc = useQueryClient();
  const onSuccess = () =>
    void qc.invalidateQueries({ queryKey: ['lab-orders'] });
  const post = (path: string, body?: unknown) =>
    api.post<FeedMessage>(path, body).then((r) => r.data);
  return {
    demo: useMutation({
      mutationFn: (unmatched: boolean) => post('/lab/feed/demo', { unmatched }),
      onSuccess,
    }),
    match: useMutation({
      mutationFn: (v: { id: string; labOrderId: string }) =>
        post(`/lab/feed/messages/${v.id}/match`, { labOrderId: v.labOrderId }),
      onSuccess,
    }),
    reject: useMutation({
      mutationFn: (v: { id: string; reason: string }) =>
        post(`/lab/feed/messages/${v.id}/reject`, { reason: v.reason }),
      onSuccess,
    }),
  };
}

export function useImagingReferrals() {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: ['imaging', 'list', branch],
    queryFn: async ({ signal }) =>
      (await api.get<ImagingReferral[]>('/imaging-referrals', { signal })).data,
    enabled: branch !== 'all',
    refetchInterval: LIVE_MS,
  });
}

export function useImagingToReview() {
  return useQuery({
    queryKey: ['imaging', 'to-review'],
    queryFn: async ({ signal }) =>
      (
        await api.get<ImagingReferral[]>('/results/imaging-to-review', {
          signal,
        })
      ).data,
    refetchInterval: LIVE_MS,
  });
}

export function useImagingActions() {
  const qc = useQueryClient();
  const onSuccess = () => {
    void qc.invalidateQueries({ queryKey: ['imaging'] });
    void qc.invalidateQueries({ queryKey: ['patient-record'] });
  };
  const post = (path: string, body: unknown) =>
    api.post<ImagingReferral>(path, body).then((r) => r.data);
  return {
    appointment: useMutation({
      mutationFn: (v: { id: string; at: string; note?: string }) =>
        post(`/imaging-referrals/${v.id}/appointment`, {
          at: v.at,
          note: v.note,
        }),
      onSuccess,
    }),
    report: useMutation({
      mutationFn: (v: { id: string; report: string }) =>
        post(`/imaging-referrals/${v.id}/report`, { report: v.report }),
      onSuccess,
    }),
    review: useMutation({
      mutationFn: (v: { id: string; comment?: string }) =>
        post(`/imaging-referrals/${v.id}/review`, { comment: v.comment }),
      onSuccess,
    }),
  };
}

export function useLabCatalogue(all = false) {
  return useQuery({
    queryKey: ['lab-tests', all],
    queryFn: async ({ signal }) =>
      (
        await api.get<CatalogueTest[]>('/lab-tests', {
          signal,
          query: all ? { all: 'true' } : undefined,
        })
      ).data,
    staleTime: 60_000,
  });
}

export function useUpdateLabTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      id: string;
      priceSen?: number | null;
      memberPriceSen?: number | null;
      panelCovered?: boolean;
      isActive?: boolean;
    }) => {
      const { id, ...body } = v;
      return api
        .patch<CatalogueTest>(`/lab-tests/${id}`, body)
        .then((r) => r.data);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lab-tests'] }),
  });
}

// ------------------------------------------------------- outside reports & trends

export function useRecentOutsideReports() {
  return useQuery({
    queryKey: ['outside-reports', 'recent'],
    queryFn: async ({ signal }) =>
      (await api.get<OutsideReport[]>('/results/outside-reports', { signal }))
        .data,
  });
}

export function useOutsideActions() {
  const qc = useQueryClient();
  return {
    read: useMutation({
      mutationFn: (text: string) =>
        api
          .post<{ reader: string; aiConnected: boolean; rows: OutsideRow[] }>(
            '/outside-reports/read',
            { text },
          )
          .then((r) => r.data),
    }),
    save: useMutation({
      mutationFn: (v: {
        patientId: string;
        source?: string;
        reportDate?: string;
        rawText?: string;
        results: OutsideRow[];
      }) => {
        const { patientId, ...body } = v;
        return api
          .post<OutsideReport>(`/patients/${patientId}/outside-reports`, body)
          .then((r) => r.data);
      },
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['outside-reports'] });
        void qc.invalidateQueries({ queryKey: ['patient-record'] });
        void qc.invalidateQueries({ queryKey: ['lab-trends'] });
      },
    }),
  };
}

export function useLabTrends(patientId: string | null) {
  return useQuery({
    queryKey: ['lab-trends', patientId],
    queryFn: async ({ signal }) =>
      (
        await api.get<{ test: string; points: TrendPoint[] }[]>(
          `/patients/${patientId}/lab-trends`,
          { signal },
        )
      ).data,
    enabled: !!patientId,
  });
}
